import React from "react";
import { Link } from "react-router-dom";

/**
 * DEV-022 · Portal-wide premium hero.
 *
 * ONE hero component shared by every role landing dashboard
 * (Sales, Operations, Installer/Field). Matches the strong
 * composition the Field Portal already established: dark navy
 * canvas with a Pearl-green accent stripe, uppercase eyebrow,
 * strong welcome/statement, benefit-led supporting sentence,
 * a primary CTA and a right-hand quiet count ring — so all four
 * dashboards feel like one product.
 *
 *   • `eyebrow`  — "YOUR WORKLOAD", "TODAY", "OVERVIEW"
 *   • `title`    — "Welcome back, Jordan" / "Today's schedule"
 *   • `sentence` — optional JSX summary sentence
 *   • `cta`      — { to, label, icon }
 *   • `ring`     — { value, label } (optional right circle)
 *   • `tag`      — small chip on the CTA row (e.g. Pearl Energy)
 *
 * All colour comes from the Pearl palette (navy #152244 →
 * #1F3A78 vertical gradient; Pearl green #3E9E2E accent).
 */
export default function PortalHero({
    eyebrow,
    title,
    sentence,
    cta,
    ring,
    tag,
    testid = "portal-hero",
}) {
    const CtaIcon = cta?.icon;
    return (
        <section
            data-testid={testid}
            className="relative overflow-hidden rounded-2xl p-7 md:p-9 flex flex-col md:flex-row md:items-center gap-6 md:gap-8 text-white"
            style={{
                background:
                    "linear-gradient(180deg,#152244 0%,#1D2E5C 55%,#1F3A78 100%)",
            }}
        >
            {/* Pearl-green accent stripe — restrained brand cue */}
            <span
                aria-hidden
                className="absolute top-0 left-0 h-1 w-24 rounded-br-full bg-[#3E9E2E]"
            />
            {/* Soft radial glow — depth without decorative clutter */}
            <span
                aria-hidden
                className="absolute -right-24 -bottom-32 w-72 h-72 rounded-full bg-[#3E9E2E]/10 blur-3xl"
            />

            <div className="flex-1 min-w-0 relative">
                {eyebrow && (
                    <div
                        className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#3E9E2E]"
                        data-testid={`${testid}-eyebrow`}
                    >
                        {eyebrow}
                    </div>
                )}
                {title && (
                    <h1
                        className="mt-2 text-2xl md:text-4xl font-semibold tracking-tight leading-tight"
                        data-testid={`${testid}-title`}
                    >
                        {title}
                    </h1>
                )}
                {sentence && (
                    <div
                        className="mt-3 text-sm md:text-base text-white/75 leading-relaxed max-w-2xl"
                        data-testid={`${testid}-sentence`}
                    >
                        {sentence}
                    </div>
                )}

                {(cta || tag) && (
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                        {cta && (
                            <Link
                                to={cta.to}
                                data-testid={`${testid}-cta`}
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#3E9E2E] text-white text-sm font-semibold hover:bg-[#348426] focus:outline-none focus:ring-2 focus:ring-white/40 transition-colors"
                            >
                                {CtaIcon && <CtaIcon size={16} strokeWidth={2} />}
                                {cta.label}
                            </Link>
                        )}
                        {tag && (
                            <span
                                data-testid={`${testid}-tag`}
                                className="inline-flex items-center gap-2 px-3 h-9 rounded-full text-xs font-medium bg-white/[0.08] border border-white/[0.14] text-white/85"
                            >
                                <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-[#3E9E2E]" />
                                {tag}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {ring && (
                <div
                    data-testid={`${testid}-ring`}
                    className="hidden md:flex shrink-0 w-36 h-36 rounded-full items-center justify-center relative"
                    style={{
                        borderWidth: 2,
                        borderStyle: "solid",
                        borderColor: "rgba(62,158,46,0.55)",
                        background: "rgba(62,158,46,0.10)",
                    }}
                >
                    <div className="text-center">
                        <div
                            className="text-4xl md:text-5xl font-semibold tracking-tight leading-none tabular-nums"
                            data-testid={`${testid}-ring-value`}
                        >
                            {ring.value ?? "—"}
                        </div>
                        <div className="mt-1.5 text-[10px] uppercase tracking-[0.16em] text-white/70">
                            {ring.label}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
